import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/brand";
import ForgotPasswordDialog from "../components/ForgotPasswordDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* brand panel */}
      <div className="relative hidden md:flex flex-col justify-between bg-secondary p-10 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1776860150305-108ed577d7d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-3">
          <img src={BRAND.logoMark} alt="" className="h-12 w-12 rounded-full" />
          <div>
            <div className="font-heading text-lg font-extrabold">{BRAND.name}</div>
            <div className="text-xs uppercase tracking-widest text-emerald-300">
              {BRAND.tagline}
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">
            Heat pump servicing,
            <br />
            done in the field.
          </h1>
          <p className="mt-4 max-w-sm text-emerald-100/80">
            Complete service reports on-site, work fully offline, and share clean
            branded PDFs with your customers.
          </p>
        </div>
        <p className="relative text-xs text-emerald-200/70">{BRAND.website}</p>
      </div>

      {/* form */}
      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center md:hidden">
            <img src={BRAND.logoMark} alt="" className="h-16 w-16 rounded-full" />
            <h1 className="mt-3 font-heading text-xl font-extrabold text-slate-900">
              {BRAND.name}
            </h1>
            <p className="text-xs uppercase tracking-widest text-primary">
              Field Service
            </p>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Engineer sign in
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Sign in to access your jobs and reports.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@steriorenewables.ie"
                className="h-12"
                data-testid="login-email"
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs font-semibold text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pr-12"
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full text-base font-semibold"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>

          <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
        </div>
      </div>
    </div>
  );
}
